/// <amd-module name="Build/Run/Components/ChangesLink" />
import * as React from "react";
import * as Resources from "Build/Run/Resources";

import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { ChangesData } from "Build/Client/Contracts/DataProvider";
import { BuildReason, Change } from "Build/Client/Contracts/Build";
import { ILinkData, ISourceProvider, RepositoryTypes } from "Build/SourceProviders/Common";
import { VssComponent, IVssComponentContext, IVssLayoutManager } from "VSS/Platform/Layout";
import { ISourceProviderService } from "Build/SourceProviders/SourceProvider";
import { format } from "VSS/Core/Util/String";
import { Button } from "VSSUI/Button";
import { ICancelReason } from "VSS/Core/Util/Promise";
import { IChangesPanelProps, ChangesPanel } from "./ChangesPanel";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export interface IChangesLinkProps {
    associatedChangesData: IReadonlyObservableValue<ChangesData>;
    repositoryName: string;
    repositoryId: string;
    repositoryUrl: string;
    sourceType: string;
    reason: BuildReason;
    branch: string;
    linkDescribedBy?: string;
}

interface IChangesLinkState {
    changes: Change[];
    changeLinks: ILinkData[];
    diffLink: string;
    changeDisplayCount: number;
}

export class ChangesLink extends VssComponent<IChangesLinkProps, IChangesLinkState> {
    private _changeIconName: string;
    private _sourceProvider: ISourceProvider;
    private _isTfvc: boolean;

    constructor(props: IChangesLinkProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            changes: this.props.associatedChangesData.value.changes,
            changeLinks: [],
            diffLink: "",
            changeDisplayCount: 0
        }

        const sourceProviderService = this.context.pageContext.getService<ISourceProviderService>("ISourceProviderService");
        this._sourceProvider = sourceProviderService.getSourceProvider(props.sourceType || "");
        this._changeIconName = this._sourceProvider ? this._sourceProvider.getCommitIconName() : "";
        this._isTfvc = this._sourceProvider.getKey() === RepositoryTypes.TfsVersionControl;
    }

    public render() {
        const count = this.state.changeDisplayCount;

        if (count) {
            const text = (count == 1) ? Resources.OneChange : format(Resources.ChangesFormat, count);
            const className = "text-ellipsis flex-row flex-baseline ";

            if (this._isTfvc) {
                return (
                    <Button ariaDescribedBy={this.props.linkDescribedBy} className={className} onClick={this._onActivateChangesPanel}>
                        {text}
                    </Button>
                );
            }

            if (this.state.diffLink) {
                const text = (count == 1) ? Resources.ViewChange : format(Resources.ViewChanges, count);
                return (
                    <Button ariaDescribedBy={this.props.linkDescribedBy} className={className} href={this.state.diffLink}>
                        {text}
                    </Button>
                );
            }

            return (
                <Button ariaDescribedBy={this.props.linkDescribedBy} className={className} disabled={true} subtle={true}>
                    {text}
                </Button>
            );
        }

        return null;
    }

    public componentDidMount() {
        super.componentDidMount();

        this.props.associatedChangesData.subscribe(this._updateChanges);
        this._updateChanges(this.props.associatedChangesData.value);
    }

    public componentWillUnmount() {
        super.componentWillUnmount();

        this.props.associatedChangesData.unsubscribe(this._updateChanges);
    }

    private _updateChanges = (value: ChangesData) => {
        const isPR = this.props.reason === BuildReason.PullRequest;
        let count = 0;

        if (value.changes) {
            count = value.changes.length;

            // if we do not have a base source version, we will end up using the first commit as a base version, subtract 1
            if (count > 1 && !isPR && !value.previousSourceVersion && !this._isTfvc) {
                count -= 1;
            }
        }
        this.setState({
            changes: value.changes,
            changeDisplayCount: count
        });


        if (value.changes && this._sourceProvider) {
            // PR build, get link to PR commits
            if (isPR) {
                this.trackPromise(
                    this._sourceProvider.getProjectName(this.props.repositoryId)
                ).promise.then((projectName: string | null) => 
                    this.trackPromise(
                        this._sourceProvider.fetchPRCommitLink({
                            repositoryName: this.props.repositoryName || this.props.repositoryId,
                            branchName: this.props.branch,
                            repositoryUrl: "",
                            links: {}
                        }, projectName ? projectName : undefined)
                    ).promise
                ).then((link: string) => {
                    this.setState({
                        diffLink: link
                    });
                }, (error: ICancelReason) => {
                    if (!error.isCanceled) {
                        console.log(error);
                    }
                });
            }
            // one commit, get link to commit
            else if (count === 1) {
                this.trackPromise(
                    this._sourceProvider.getProjectName(this.props.repositoryId)
                ).promise.then((projectName: string | null) => {
                    if (!this._isTfvc) {
                        this.trackPromise(
                            this._sourceProvider.fetchCommitLink({
                                repositoryName: this.props.repositoryName || this.props.repositoryId,
                                commitId: value.changes[0].id,
                                displayUri: ""
                            }, projectName ? projectName : undefined)
                        ).promise.then((link: string) => {
                            this.setState({
                                diffLink: link
                            });
                        }, (error: ICancelReason) => {
                            if (!error.isCanceled) {
                                console.log(error);
                            }
                        })
                    }
                    else {
                        this.fetchTfvcChangesetLinks(projectName);
                    }
                });
            }
            // more than one commit, get link to a diff between them
            else if (count > 1) {
                this.trackPromise(
                    this._sourceProvider.getProjectName(this.props.repositoryId)
                ).promise.then((projectName: string | null) => {
                    if (!this._isTfvc) {
                        const baseChange = value.changes[value.changes.length - 1];
                        const targetChange = value.changes[0];
                        this.trackPromise(
                            this._sourceProvider.fetchCommitDiffLink({
                                repositoryName: this.props.repositoryName || this.props.repositoryId,
                                baseCommitId: value.previousSourceVersion || baseChange.id,
                                targetCommitId: targetChange.id,
                                displayUri: ""
                            }, projectName ? projectName : undefined)
                        ).promise.then((link: string) => {
                            this.setState({
                                diffLink: link
                            });
                        }, (error: ICancelReason) => {
                            if (!error.isCanceled) {
                                console.log(error);
                            }
                        });
                    }
                    else {
                        this.fetchTfvcChangesetLinks(projectName);
                    }
                });
            }
        }
    }

    private async fetchTfvcChangesetLinks(projectName: string | null) {
        this._sourceProvider.fetchLinks(this.state.changes.map(change => {
            return {
                branchName: this.props.branch,
                repositoryName: this.props.repositoryName || this.props.repositoryId,
                repositoryUrl: this.props.repositoryUrl,
                commitId: change.id,
                displayUri: change.displayUri || "",
                projectName: projectName ? projectName : undefined
            };
        })).then((links) => {
            if (links) {
                this.setState({
                    changeLinks: links
                });
            }
        });
    }

    private _onActivateChangesPanel = () => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss) => {
            const props: IChangesPanelProps = {
                changes: this.state.changes,
                changeLinks: this.state.changeLinks,
                onDismiss: onDismiss,
                repositoryName: this.props.repositoryName,
                repositoryType: this.props.sourceType,
                sourceProvider: this._sourceProvider
            }

            return React.createElement(ChangesPanel, props);
        });
    }
}

export class SubtleChangesLink extends VssComponent<IChangesLinkProps, IChangesLinkState> {
    private _sourceProvider: ISourceProvider;

    constructor(props: IChangesLinkProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            changes: this.props.associatedChangesData.value.changes,
            changeLinks: [],
            diffLink: "",
            changeDisplayCount: 0
        }

        const sourceProviderService = this.context.pageContext.getService<ISourceProviderService>("ISourceProviderService");
        this._sourceProvider = sourceProviderService.getSourceProvider(props.sourceType || "");
    }

    public render() {
        const count = this.state.changeDisplayCount;

        if (count) {
            const text = (count == 1) ? Resources.SourceCardCommitLabel : format(Resources.SourceCardCommitsLabel, count);
            const className = "text-ellipsis flex-row flex-baseline ";

            return (
                <FPSLink className={className} href={this.state.diffLink} subtle={true} >
                    <span className="text-ellipsis ">
                        {text}
                    </span>
                </FPSLink >);
        }
        else {
            return (
                <Spinner size={SpinnerSize.xSmall}/>
            );
        }
    }

    public componentDidMount() {
        super.componentDidMount();

        this.props.associatedChangesData.subscribe(this._updateChanges);
        this._updateChanges(this.props.associatedChangesData.value);
    }

    public componentWillUnmount() {
        super.componentWillUnmount();

        this.props.associatedChangesData.unsubscribe(this._updateChanges);
    }

    private _updateChanges = (value: ChangesData) => {
        const isPR = this.props.reason === BuildReason.PullRequest;
        let count = 0;

        if (value.changes) {
            count = value.changes.length;

            // if we do not have a base source version, we will end up using the first commit as a base version, subtract 1
            if (count > 1 && !isPR && !value.previousSourceVersion) {
                count -= 1;
            }
        }

        this.setState({
            changes: value.changes,
            changeDisplayCount: count
        });

        if (value.changes && this._sourceProvider) {
            // PR build, get link to PR commits
            if (isPR) {
                this.trackPromise(
                    this._sourceProvider.getProjectName(this.props.repositoryId)
                ).promise.then((projectName: string | null) => 
                    this.trackPromise(
                        this._sourceProvider.fetchPRCommitLink({
                            repositoryName: this.props.repositoryName || this.props.repositoryId,
                            branchName: this.props.branch,
                            repositoryUrl: "",
                            links: {}
                        }, projectName ? projectName : undefined)
                    ).promise
                ).then((link: string) => {
                    this.setState({
                        diffLink: link
                    });
                }, (error: ICancelReason) => {
                    if (!error.isCanceled) {
                        console.log(error);
                    }
                });
            }
            // one commit, get link to commit
            else if (count === 1) {
                this.trackPromise(
                    this._sourceProvider.getProjectName(this.props.repositoryId)
                ).promise.then((projectName: string | null) => 
                    this.trackPromise(
                        this._sourceProvider.fetchCommitLink({
                            repositoryName: this.props.repositoryName || this.props.repositoryId,
                            commitId: value.changes[0].id,
                            displayUri: ""
                        }, projectName ? projectName : undefined)
                    ).promise
                ).then((link: string) => {
                    this.setState({
                        diffLink: link
                    });
                }, (error: ICancelReason) => {
                    if (!error.isCanceled) {
                        console.log(error);
                    }
                });
            }
            // more than one commit, get link to a diff between them
            else if (count > 1) {
                this.trackPromise(
                    this._sourceProvider.getProjectName(this.props.repositoryId)
                ).promise.then((projectName: string | null) => {
                    const baseChange = value.changes[value.changes.length - 1];
                    const targetChange = value.changes[0];
                    this.trackPromise(
                        this._sourceProvider.fetchCommitDiffLink({
                            repositoryName: this.props.repositoryName || this.props.repositoryId,
                            baseCommitId: value.previousSourceVersion || baseChange.id,
                            targetCommitId: targetChange.id,
                            displayUri: ""
                        }, projectName ? projectName : undefined)
                    ).promise.then((link: string) => {
                        this.setState({
                            diffLink: link
                        });
                    }, (error: ICancelReason) => {
                        if (!error.isCanceled) {
                            console.log(error);
                        }
                    });
                });
            }
        }
    }
}
